<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { kdm, judgmentOptions, getIndicatorInfo, discussionState } from '@/store/kdm';
import { useFetch } from '@/composables/useFetch';
import SpinnerIcon from '@/icons/SpinnerIcon.vue';

const props = defineProps<{
  visible: boolean;
  groupLogo?: string;
  groupTitle?: string;
  canWrite?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const ofetch = useFetch();

// Display state
const showComments = ref(false);
const loading = ref(false);

// Max conversations shown per row before truncation; toggled from the column header.
const COMMENT_LIMIT = 5;
const showAllComments = ref(false);

// Sorting
type SortField = 'ipr' | 'evaluation' | 'stakeholder' | 'toc' | 'qpi';
const sortField = ref<SortField>('ipr');
const sortDirection = ref<'asc' | 'desc'>('asc');

// Column order (reorderable)
type ColumnId = 'ipr' | 'evaluation' | 'stakeholder' | 'toc' | 'qpi' | 'comment';
const columnOrder = ref<ColumnId[]>(['ipr', 'evaluation', 'stakeholder', 'toc', 'qpi', 'comment']);

const columnLabels: Record<ColumnId, string> = {
  ipr: 'IPR',
  evaluation: 'Evaluation Value',
  stakeholder: 'Stakeholder',
  toc: 'TOC',
  qpi: 'QPI',
  comment: 'Comment',
};

function moveColumn(colId: ColumnId, direction: 'left' | 'right') {
  const idx = columnOrder.value.indexOf(colId);
  if (idx === -1) return;
  const newIdx = direction === 'left' ? idx - 1 : idx + 1;
  if (newIdx < 0 || newIdx >= columnOrder.value.length) return;
  const newOrder = [...columnOrder.value];
  [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
  columnOrder.value = newOrder;
}

function canMoveColumn(colId: ColumnId, direction: 'left' | 'right'): boolean {
  const idx = columnOrder.value.indexOf(colId);
  if (direction === 'left') return idx > 0;
  return idx < columnOrder.value.length - 1;
}

// Visible columns (excludes comment if hidden)
const visibleColumns = computed(() => {
  return columnOrder.value.filter(col => col !== 'comment' || showComments.value);
});

// Filtering
const filters = ref({
  ipr: '' as string,
  stakeholder: '' as string,
  toc: '' as string,
  qpi: '' as string,
  evaluation: '' as string,
});

// Comments cache (counts only)
const commentsCache = ref<Record<string, number>>({});

// Full comment conversations, keyed by coords. Loaded lazily the first time the
// Comment column is revealed, so the column can show the threads inline.
const commentThreads = ref<Record<string, any[]>>({});
const threadsLoading = ref(false);

// Data structure for flat rows
interface KnotViewRow {
  iprId: string;
  iprTitle: string;
  iprHref: string;
  evaluationId: string;
  evaluationLabel: string;
  evaluationColor: string;
  sliderValue: number;
  stakeholderId: string;
  stakeholderTitle: string;
  tocId: string;
  tocTitle: string;
  qpiId: string;
  qpiTitle: string;
  cellCoords: string;
  indicatorCoords: string;
  commentCount: number;
}

// Parse matrix data into flat rows
const allRows = computed<KnotViewRow[]>(() => {
  if (!kdm.value) return [];

  const rows: KnotViewRow[] = [];
  let matrixData: Record<string, any> = {};

  if (kdm.value.field_matrix_data) {
    try {
      matrixData = typeof kdm.value.field_matrix_data === 'string'
        ? JSON.parse(kdm.value.field_matrix_data)
        : kdm.value.field_matrix_data;
    } catch (e) {
      return rows;
    }
  }

  const stakeholders = kdm.value.stake_holders || [];
  const qpis = kdm.value.rows || [];
  const tocs = kdm.value.columns || [];

  // Build lookup maps
  const shMap = new Map(stakeholders.map(s => [s.id, s]));
  const qpiMap = new Map(qpis.map(q => [q.id, q]));
  const tocMap = new Map(tocs.map(t => [t.id, t]));

  // Iterate through all cells
  Object.entries(matrixData).forEach(([cellKey, cellData]) => {
    if (cellKey === 'assignments' || !cellData || typeof cellData !== 'object') return;

    // Parse cell key: SH-QPI-TOC
    const parts = cellKey.split('-');
    if (parts.length < 3) return;

    const [shId, qpiId, tocId] = parts;
    const sh = shMap.get(shId);
    const qpi = qpiMap.get(qpiId);
    const toc = tocMap.get(tocId);

    if (!sh || !qpi || !toc) return;

    // For Type 1: single value per cell
    if (kdm.value?.kdm_type === 1) {
      if (cellData.judgmentId) {
        const judgment = judgmentOptions.value.get(cellData.judgmentId);
        rows.push({
          iprId: 'direct',
          iprTitle: 'Direct Evaluation',
          iprHref: '',
          evaluationId: cellData.judgmentId,
          evaluationLabel: judgment?.title || judgment?.color || '',
          evaluationColor: judgment?.color || '#ccc',
          sliderValue: cellData.sliderValue || 0,
          stakeholderId: shId,
          stakeholderTitle: sh.title,
          tocId: tocId,
          tocTitle: toc.title,
          qpiId: qpiId,
          qpiTitle: qpi.title,
          cellCoords: cellKey,
          indicatorCoords: cellKey,
          commentCount: commentsCache.value[cellKey] || kdm.value?.comment_summary?.[cellKey]?.count || 0,
        });
      }
      return;
    }

    // For Type 2/3: multiple indicators per cell
    Object.entries(cellData).forEach(([indicatorId, indicatorData]: [string, any]) => {
      if (!indicatorData || typeof indicatorData !== 'object' || !indicatorData.judgmentId) return;

      const info = getIndicatorInfo(indicatorId);
      const judgment = judgmentOptions.value.get(indicatorData.judgmentId);
      const indicatorCoords = `${cellKey}-${indicatorId}`;

      rows.push({
        iprId: indicatorId,
        iprTitle: info.title || indicatorId,
        iprHref: info.href || `/node/${indicatorId}`,
        evaluationId: indicatorData.judgmentId,
        evaluationLabel: judgment?.title || judgment?.color || '',
        evaluationColor: judgment?.color || '#ccc',
        sliderValue: indicatorData.sliderValue || 0,
        stakeholderId: shId,
        stakeholderTitle: sh.title,
        tocId: tocId,
        tocTitle: toc.title,
        qpiId: qpiId,
        qpiTitle: qpi.title,
        cellCoords: cellKey,
        indicatorCoords: indicatorCoords,
        commentCount: commentsCache.value[indicatorCoords] || kdm.value?.comment_summary?.[indicatorCoords]?.count || 0,
      });
    });
  });

  return rows;
});

// Filtered rows
const filteredRows = computed(() => {
  return allRows.value.filter(row => {
    if (filters.value.ipr && !row.iprTitle.toLowerCase().includes(filters.value.ipr.toLowerCase())) return false;
    if (filters.value.stakeholder && row.stakeholderId !== filters.value.stakeholder) return false;
    if (filters.value.toc && row.tocId !== filters.value.toc) return false;
    if (filters.value.qpi && row.qpiId !== filters.value.qpi) return false;
    if (filters.value.evaluation && row.evaluationId !== filters.value.evaluation) return false;
    return true;
  });
});

// Sorted rows
const sortedRows = computed(() => {
  const rows = [...filteredRows.value];
  const dir = sortDirection.value === 'asc' ? 1 : -1;

  rows.sort((a, b) => {
    let cmp = 0;
    switch (sortField.value) {
      case 'ipr':
        cmp = a.iprTitle.localeCompare(b.iprTitle);
        break;
      case 'evaluation':
        cmp = a.evaluationLabel.localeCompare(b.evaluationLabel) || (a.sliderValue - b.sliderValue);
        break;
      case 'stakeholder':
        cmp = a.stakeholderTitle.localeCompare(b.stakeholderTitle);
        break;
      case 'toc':
        cmp = a.tocTitle.localeCompare(b.tocTitle);
        break;
      case 'qpi':
        cmp = a.qpiTitle.localeCompare(b.qpiTitle);
        break;
    }
    return cmp * dir;
  });

  return rows;
});

// Group rows by IPR for display with subtotals
interface IprGroup {
  iprId: string;
  iprTitle: string;
  iprHref: string;
  rows: KnotViewRow[];
  totals: Map<string, { count: number; totalWeight: number }>;
}

const groupedByIpr = computed<IprGroup[]>(() => {
  const groups = new Map<string, IprGroup>();

  sortedRows.value.forEach(row => {
    if (!groups.has(row.iprId)) {
      groups.set(row.iprId, {
        iprId: row.iprId,
        iprTitle: row.iprTitle,
        iprHref: row.iprHref,
        rows: [],
        totals: new Map(),
      });
    }
    const group = groups.get(row.iprId)!;
    group.rows.push(row);

    // Update totals per evaluation value
    if (!group.totals.has(row.evaluationId)) {
      group.totals.set(row.evaluationId, { count: 0, totalWeight: 0 });
    }
    const total = group.totals.get(row.evaluationId)!;
    total.count++;
    total.totalWeight += row.sliderValue;
  });

  return Array.from(groups.values());
});

// Collapsible IPR detail groups (empty set = all expanded by default).
const collapsedGroups = ref<Set<string>>(new Set());

function toggleGroup(id: string) {
  const next = new Set(collapsedGroups.value);
  next.has(id) ? next.delete(id) : next.add(id);
  collapsedGroups.value = next;
}

const allCollapsed = computed(() =>
  groupedByIpr.value.length > 0 && groupedByIpr.value.every(g => collapsedGroups.value.has(g.iprId))
);

function toggleAllGroups() {
  collapsedGroups.value = allCollapsed.value
    ? new Set()
    : new Set(groupedByIpr.value.map(g => g.iprId));
}

// An IPR that still exists but is no longer linked to the KIK (moved/contextualised
// elsewhere). Shown in red, consistent with the matrix and the selection drawer.
function isOrphanIpr(iprId: string): boolean {
  return getIndicatorInfo(iprId).is_in_kik === false;
}

// Unique values for filters
const uniqueStakeholders = computed(() => {
  return kdm.value?.stake_holders || [];
});

const uniqueTocs = computed(() => {
  return kdm.value?.columns || [];
});

const uniqueQpis = computed(() => {
  return kdm.value?.rows || [];
});

const uniqueEvaluations = computed(() => {
  return Array.from(judgmentOptions.value.entries()).map(([id, opt]) => ({
    id,
    label: opt.title || opt.color,
    color: opt.color,
  }));
});

// Summary statistics
const summary = computed(() => {
  const stats = {
    totalRows: sortedRows.value.length,
    byEvaluation: new Map<string, { count: number; percentage: number }>(),
    totalComments: 0,
  };

  sortedRows.value.forEach(row => {
    if (!stats.byEvaluation.has(row.evaluationId)) {
      stats.byEvaluation.set(row.evaluationId, { count: 0, percentage: 0 });
    }
    stats.byEvaluation.get(row.evaluationId)!.count++;
    stats.totalComments += row.commentCount;
  });

  // Calculate percentages
  stats.byEvaluation.forEach((value) => {
    value.percentage = stats.totalRows > 0 ? Math.round((value.count / stats.totalRows) * 100) : 0;
  });

  return stats;
});

// Sorting handler
function toggleSort(field: SortField) {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortDirection.value = 'asc';
  }
}

function getSortIcon(field: SortField) {
  if (sortField.value !== field) return 'bi-chevron-expand';
  return sortDirection.value === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
}

// Clear all filters
function clearFilters() {
  filters.value = {
    ipr: '',
    stakeholder: '',
    toc: '',
    qpi: '',
    evaluation: '',
  };
}

const hasActiveFilters = computed(() => {
  return Object.values(filters.value).some(v => v !== '');
});

// Load the full comment conversations for every row that has comments, so the
// Comment column can show the threads inline (not just the count). Fetches each
// distinct coords once, in parallel, reusing the same endpoint as DiscussionPanel.
async function loadCommentThreads() {
  const nid = kdm.value?.nid;
  if (!nid) return;
  const coordsList = Array.from(
    new Set(allRows.value.filter(r => r.commentCount > 0).map(r => r.indicatorCoords))
  ).filter(coords => !(coords in commentThreads.value));
  if (coordsList.length === 0) return;

  threadsLoading.value = true;
  try {
    await Promise.all(coordsList.map(async (coords) => {
      try {
        const res = await ofetch<any>(`/epbapi/comments/${nid}?coords=${encodeURIComponent(coords)}`);
        const list = Array.isArray(res) ? res : (res?.comments ?? []);
        commentThreads.value = { ...commentThreads.value, [coords]: list };
      } catch (e) {
        commentThreads.value = { ...commentThreads.value, [coords]: [] };
      }
    }));
  } finally {
    threadsLoading.value = false;
  }
}

// Fetch the conversations the first time the user reveals the Comment column.
watch(showComments, (on) => {
  if (on) loadCommentThreads();
});

// Re-fetch a single row's conversation (e.g. after a message is posted in the drawer).
async function refreshThread(coords: string) {
  const nid = kdm.value?.nid;
  if (!nid || !coords) return;
  try {
    const res = await ofetch<any>(`/epbapi/comments/${nid}?coords=${encodeURIComponent(coords)}`);
    const list = Array.isArray(res) ? res : (res?.comments ?? []);
    commentThreads.value = { ...commentThreads.value, [coords]: list };
  } catch (e) {
    // Keep the previously loaded thread on error.
  }
}

// When the comment count of the row whose drawer is open changes (a message was
// posted/loaded there), refresh that row's inline conversation so it stays in
// sync. The count itself is already reactive via comment_summary.
watch(
  () => kdm.value?.comment_summary?.[discussionState.coords]?.count,
  (count, prev) => {
    const coords = discussionState.coords;
    if (coords && count !== prev && coords in commentThreads.value) {
      refreshThread(coords);
    }
  }
);

// Open the shared discussion drawer for a row, exactly like a matrix cell does.
function openDiscussion(coords: string) {
  discussionState.coords = coords;
  discussionState.nodeId = kdm.value?.nid || '';
  discussionState.canWrite = props.canWrite ?? false;
  discussionState.visible = true;
}

// Pick a readable text color (black/white) for a given background color, so light
// judgment colors (e.g. yellow) are not rendered white-on-light and unreadable.
function readableTextColor(bg?: string): string {
  if (!bg) return '#fff';
  const s = bg.trim();
  let r = 0, g = 0, b = 0;
  const m6 = /^#([0-9a-f]{6})$/i.exec(s);
  const m3 = /^#([0-9a-f]{3})$/i.exec(s);
  const rgb = /^rgba?\(([^)]+)\)/i.exec(s);
  if (m6) { r = parseInt(s.slice(1, 3), 16); g = parseInt(s.slice(3, 5), 16); b = parseInt(s.slice(5, 7), 16); }
  else if (m3) { r = parseInt(s[1] + s[1], 16); g = parseInt(s[2] + s[2], 16); b = parseInt(s[3] + s[3], 16); }
  else if (rgb) { const p = rgb[1].split(',').map(n => parseFloat(n)); r = p[0] || 0; g = p[1] || 0; b = p[2] || 0; }
  else return '#fff';
  // Perceived luminance (0..1): bright backgrounds get dark text.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1a1a1a' : '#fff';
}

// Print function
function printKnotView() {
  window.print();
}

// Format totals for display
function formatTotals(totals: Map<string, { count: number; totalWeight: number }>) {
  const parts: string[] = [];
  totals.forEach((value, evalId) => {
    const judgment = judgmentOptions.value.get(evalId);
    const label = judgment?.title || judgment?.color || evalId;
    const avgWeight = value.count > 0 ? Math.round(value.totalWeight / value.count) : 0;
    parts.push(`${label} (${value.count}x, avg ${avgWeight}%)`);
  });
  return parts.join(', ');
}

// Get current date formatted
const currentDate = computed(() => {
  return new Date().toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});
</script>

<template>
  <div v-if="visible" class="knot-view">
    <!-- Print Header (only visible when printing) -->
    <div class="knot-view__print-header">
      <div class="knot-view__print-header-left">
        <img v-if="groupLogo" :src="groupLogo" alt="Group Logo" class="knot-view__print-logo" />
        <div class="knot-view__print-info">
          <div v-if="groupTitle" class="knot-view__print-group">{{ groupTitle }}</div>
          <h1 class="knot-view__print-title">{{ kdm?.title }} - Knot View</h1>
          <div class="knot-view__print-date">{{ currentDate }}</div>
        </div>
      </div>
      <div class="knot-view__print-filters" v-if="hasActiveFilters">
        <strong>Filtres actifs:</strong>
        <span v-if="filters.ipr">IPR: "{{ filters.ipr }}"</span>
        <span v-if="filters.stakeholder">Stakeholder: {{ uniqueStakeholders.find(s => s.id === filters.stakeholder)?.title }}</span>
        <span v-if="filters.toc">TOC: {{ uniqueTocs.find(t => t.id === filters.toc)?.title }}</span>
        <span v-if="filters.qpi">QPI: {{ uniqueQpis.find(q => q.id === filters.qpi)?.title }}</span>
        <span v-if="filters.evaluation">Evaluation: {{ uniqueEvaluations.find(e => e.id === filters.evaluation)?.label }}</span>
      </div>
    </div>

    <!-- Screen Header -->
    <div class="knot-view__header">
      <div class="knot-view__header-left">
        <h2 class="knot-view__title">
          <i class="bi bi-table me-2"></i>
          Knot View
        </h2>
        <span class="knot-view__subtitle">
          {{ sortedRows.length }} evaluations
          <template v-if="sortedRows.length !== allRows.length">
            ({{ allRows.length }} total)
          </template>
        </span>
      </div>
      <div class="knot-view__header-right">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="toggleAllGroups"
          :title="allCollapsed ? 'Expand all indicators' : 'Collapse all indicators'"
        >
          <i class="bi me-1" :class="allCollapsed ? 'bi-arrows-expand' : 'bi-arrows-collapse'"></i>
          {{ allCollapsed ? 'Expand all' : 'Collapse all' }}
        </button>
        <button
          type="button"
          class="btn btn-sm"
          :class="showComments ? 'btn-primary' : 'btn-outline-secondary'"
          @click="showComments = !showComments"
          title="Toggle comments"
        >
          <i class="bi bi-chat-dots me-1"></i>
          Comments
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          @click="printKnotView"
          title="Print"
        >
          <i class="bi bi-printer me-1"></i>
          Print
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="$emit('close')"
          title="Close"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="knot-view__filters">
      <div class="knot-view__filter">
        <label class="knot-view__filter-label">IPR</label>
        <input
          v-model="filters.ipr"
          type="text"
          class="form-control form-control-sm"
          placeholder="Search..."
        />
      </div>
      <div class="knot-view__filter">
        <label class="knot-view__filter-label">Stakeholder</label>
        <select v-model="filters.stakeholder" class="form-select form-select-sm">
          <option value="">All</option>
          <option v-for="sh in uniqueStakeholders" :key="sh.id" :value="sh.id">
            {{ sh.title }}
          </option>
        </select>
      </div>
      <div class="knot-view__filter">
        <label class="knot-view__filter-label">TOC</label>
        <select v-model="filters.toc" class="form-select form-select-sm">
          <option value="">All</option>
          <option v-for="toc in uniqueTocs" :key="toc.id" :value="toc.id">
            {{ toc.title }}
          </option>
        </select>
      </div>
      <div class="knot-view__filter">
        <label class="knot-view__filter-label">QPI</label>
        <select v-model="filters.qpi" class="form-select form-select-sm">
          <option value="">All</option>
          <option v-for="qpi in uniqueQpis" :key="qpi.id" :value="qpi.id">
            {{ qpi.title }}
          </option>
        </select>
      </div>
      <div class="knot-view__filter">
        <label class="knot-view__filter-label">Evaluation</label>
        <select v-model="filters.evaluation" class="form-select form-select-sm">
          <option value="">All</option>
          <option v-for="ev in uniqueEvaluations" :key="ev.id" :value="ev.id">
            {{ ev.label }}
          </option>
        </select>
      </div>
      <div class="knot-view__filter knot-view__filter--actions">
        <button
          v-if="hasActiveFilters"
          type="button"
          class="btn btn-sm btn-outline-danger"
          @click="clearFilters"
        >
          <i class="bi bi-x-circle me-1"></i>
          Clear
        </button>
      </div>
    </div>

    <!-- Summary Statistics -->
    <div class="knot-view__summary">
      <div class="knot-view__summary-item">
        <strong>Total:</strong> {{ summary.totalRows }} evaluations
      </div>
      <div class="knot-view__summary-item" v-for="[evalId, stats] in summary.byEvaluation" :key="evalId">
        <span
          class="knot-view__summary-badge"
          :style="{ backgroundColor: judgmentOptions.get(evalId)?.color, color: readableTextColor(judgmentOptions.get(evalId)?.color) }"
        >
          {{ judgmentOptions.get(evalId)?.title || judgmentOptions.get(evalId)?.color }}
        </span>
        {{ stats.count }} ({{ stats.percentage }}%)
      </div>
      <div class="knot-view__summary-item" v-if="showComments">
        <i class="bi bi-chat-dots me-1"></i>
        {{ summary.totalComments }} comments
      </div>
    </div>

    <!-- Table -->
    <div class="knot-view__table-wrapper">
      <table class="knot-view__table">
        <thead>
          <tr>
            <th
              v-for="col in visibleColumns"
              :key="col"
              class="knot-view__th"
              :class="{
                'knot-view__th--sortable': col !== 'comment',
                'knot-view__th--comment': col === 'comment'
              }"
              @click="col !== 'comment' && toggleSort(col as SortField)"
            >
              <span class="knot-view__th-content">
                <button
                  v-if="canMoveColumn(col, 'left')"
                  type="button"
                  class="knot-view__move-btn"
                  @click.stop="moveColumn(col, 'left')"
                  title="Move left"
                >
                  <i class="bi bi-chevron-left"></i>
                </button>
                <span class="knot-view__th-label" @click="col !== 'comment' && toggleSort(col as SortField)">
                  {{ columnLabels[col] }}
                  <i v-if="col !== 'comment'" class="bi" :class="getSortIcon(col as SortField)"></i>
                </span>
                <button
                  v-if="col === 'comment'"
                  type="button"
                  class="knot-view__col-toggle"
                  @click.stop="showAllComments = !showAllComments"
                  :title="showAllComments ? `Show ${COMMENT_LIMIT} per row` : 'Show all messages'"
                >
                  <i class="bi" :class="showAllComments ? 'bi-chevron-bar-up' : 'bi-chevron-bar-down'"></i>
                </button>
                <button
                  v-if="canMoveColumn(col, 'right')"
                  type="button"
                  class="knot-view__move-btn"
                  @click.stop="moveColumn(col, 'right')"
                  title="Move right"
                >
                  <i class="bi bi-chevron-right"></i>
                </button>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groupedByIpr" :key="group.iprId">
            <!-- IPR Group Header -->
            <tr class="knot-view__group-header" @click="toggleGroup(group.iprId)">
              <td :colspan="visibleColumns.length">
                <button
                  type="button"
                  class="knot-view__group-toggle"
                  @click.stop="toggleGroup(group.iprId)"
                  :title="collapsedGroups.has(group.iprId) ? 'Expand' : 'Collapse'"
                >
                  <i class="bi" :class="collapsedGroups.has(group.iprId) ? 'bi-chevron-right' : 'bi-chevron-down'"></i>
                </button>
                <a
                  v-if="group.iprHref"
                  :href="group.iprHref"
                  target="_blank"
                  class="knot-view__group-title"
                  :class="{ 'fw-bold': isOrphanIpr(group.iprId) }"
                  :title="isOrphanIpr(group.iprId) ? 'Indicator/Argument no longer contextualized in the KIK' : ''"
                  @click.stop
                >
                  {{ group.iprTitle }}
                  <i class="bi bi-box-arrow-up-right ms-1 small"></i>
                </a>
                <span
                  v-else
                  class="knot-view__group-title"
                  :class="{ 'fw-bold': isOrphanIpr(group.iprId) }"
                  :title="isOrphanIpr(group.iprId) ? 'Indicator/Argument no longer contextualized in the KIK' : ''"
                >{{ group.iprTitle }}</span>
              </td>
            </tr>
            <!-- Data Rows + Totals (collapsible per indicator) -->
            <template v-if="!collapsedGroups.has(group.iprId)">
            <tr v-for="(row, idx) in group.rows" :key="row.indicatorCoords" class="knot-view__row" :class="{ 'knot-view__row--alt': idx % 2 === 1 }">
              <td
                v-for="col in visibleColumns"
                :key="col"
                class="knot-view__td"
                :class="{
                  'knot-view__td--ipr': col === 'ipr',
                  'knot-view__td--evaluation': col === 'evaluation',
                  'knot-view__td--comment': col === 'comment'
                }"
              >
                <!-- IPR: Empty - shown in group header -->
                <template v-if="col === 'ipr'"></template>
                <!-- Evaluation -->
                <template v-else-if="col === 'evaluation'">
                  <span class="knot-view__evaluation-badge" :style="{ backgroundColor: row.evaluationColor, color: readableTextColor(row.evaluationColor) }">
                    {{ row.evaluationLabel }}
                  </span>
                  <span class="knot-view__weight">({{ row.sliderValue }}%)</span>
                </template>
                <!-- Stakeholder -->
                <template v-else-if="col === 'stakeholder'">{{ row.stakeholderTitle }}</template>
                <!-- TOC -->
                <template v-else-if="col === 'toc'">{{ row.tocTitle }}</template>
                <!-- QPI -->
                <template v-else-if="col === 'qpi'">{{ row.qpiTitle }}</template>
                <!-- Comment -->
                <template v-else-if="col === 'comment'">
                  <div v-if="row.commentCount > 0" class="knot-view__comments">
                    <button
                      type="button"
                      class="knot-view__comment-count knot-view__comment-count--btn"
                      @click="openDiscussion(row.indicatorCoords)"
                      title="Open discussion"
                    >
                      <i class="bi bi-chat-dots me-1"></i>{{ row.commentCount }}
                    </button>
                    <div v-if="commentThreads[row.indicatorCoords]?.length" class="knot-view__thread">
                      <div
                        v-for="c in (showAllComments ? commentThreads[row.indicatorCoords] : commentThreads[row.indicatorCoords].slice(0, COMMENT_LIMIT))"
                        :key="c.id"
                        class="knot-view__comment"
                      >
                        <div class="knot-view__comment-head">
                          <strong>{{ c.author }}</strong>
                          <span class="knot-view__comment-date">{{ c.date }}</span>
                        </div>
                        <div class="knot-view__comment-body" v-html="c.body"></div>
                        <div
                          v-for="child in (c.children || [])"
                          :key="child.id"
                          class="knot-view__comment knot-view__comment--child"
                        >
                          <div class="knot-view__comment-head">
                            <strong>{{ child.author }}</strong>
                            <span class="knot-view__comment-date">{{ child.date }}</span>
                          </div>
                          <div class="knot-view__comment-body" v-html="child.body"></div>
                        </div>
                      </div>
                      <button
                        v-if="!showAllComments && commentThreads[row.indicatorCoords].length > COMMENT_LIMIT"
                        type="button"
                        class="knot-view__thread-more"
                        @click="showAllComments = true"
                        title="Show all messages"
                      >
                        + {{ commentThreads[row.indicatorCoords].length - COMMENT_LIMIT }} more…
                      </button>
                    </div>
                    <span v-else-if="threadsLoading" class="knot-view__thread-loading">
                      <SpinnerIcon />
                    </span>
                  </div>
                </template>
              </td>
            </tr>
            <!-- IPR Group Totals -->
            <tr class="knot-view__group-totals">
              <td class="knot-view__td knot-view__td--totals-label">Totals</td>
              <td :colspan="visibleColumns.length - 1" class="knot-view__td knot-view__td--totals">
                {{ formatTotals(group.totals) }}
              </td>
            </tr>
            </template>
          </template>
          <tr v-if="sortedRows.length === 0">
            <td :colspan="visibleColumns.length" class="knot-view__empty">
              <i class="bi bi-inbox me-2"></i>
              No evaluations found
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.knot-view {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.knot-view__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
}

.knot-view__header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.knot-view__title {
  margin: 0;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  font-size: 1.25rem;
  font-weight: 600;
}

.knot-view__subtitle {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.knot-view__header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.knot-view__header-right .btn {
  color: white;
  border-color: rgba(255, 255, 255, 0.5);
}

.knot-view__header-right .btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: white;
}

.knot-view__header-right .btn-primary {
  background: rgba(255, 255, 255, 0.2);
  border-color: transparent;
}

/* Filters */
.knot-view__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.knot-view__filter {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
}

.knot-view__filter--actions {
  justify-content: flex-end;
  min-width: auto;
}

.knot-view__filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
}

/* Summary */
.knot-view__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: #e9ecef;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.85rem;
}

.knot-view__summary-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.knot-view__summary-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Table */
.knot-view__table-wrapper {
  max-height: 60vh;
  overflow: auto;
}

.knot-view__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.knot-view__th {
  position: sticky;
  top: 0;
  background: #495057;
  color: #fff;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
  z-index: 10;
}

.knot-view__th--sortable {
  cursor: pointer;
  user-select: none;
}

.knot-view__th--sortable:hover {
  background: #5a6268;
}

.knot-view__th-content {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.knot-view__th-label {
  flex: 1;
  cursor: pointer;
}

.knot-view__move-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  padding: 0.1rem 0.2rem;
  cursor: pointer;
  border-radius: 3px;
  line-height: 1;
  font-size: 0.7rem;
}

.knot-view__move-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.knot-view__th--comment {
  width: 280px;
  min-width: 240px;
  text-align: left;
}

.knot-view__td {
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.knot-view__td--ipr {
  width: 50px;
}

.knot-view__td--evaluation {
  white-space: nowrap;
}

.knot-view__td--comment {
  text-align: left;
  font-size: 0.8rem;
  color: #6c757d;
  vertical-align: top;
}

.knot-view__row--alt {
  background: #f8f9fa;
}

/* Group header */
.knot-view__group-header {
  background: #e3f2fd;
  cursor: pointer;
}

.knot-view__group-header:hover {
  background: #d3e9fc;
}

.knot-view__group-toggle {
  background: transparent;
  border: none;
  color: #1565c0;
  padding: 0 0.4rem 0 0;
  cursor: pointer;
  line-height: 1;
}

.knot-view__group-header td {
  padding: 0.5rem 1rem;
  font-weight: 600;
  border-bottom: 2px solid #90caf9;
}

.knot-view__group-title {
  color: #1565c0;
  text-decoration: none;
}

.knot-view__group-title:hover {
  text-decoration: underline;
}

/* Group totals */
.knot-view__group-totals {
  background: #fff3e0;
  font-style: italic;
}

.knot-view__td--totals-label {
  font-weight: 600;
  color: #e65100;
}

.knot-view__td--totals {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Evaluation badge */
.knot-view__evaluation-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 500;
}

.knot-view__weight {
  margin-left: 0.5rem;
  color: #6c757d;
  font-size: 0.8rem;
}

/* Comment count */
.knot-view__comment-count {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  background: #e3f2fd;
  border-radius: 10px;
  font-size: 0.75rem;
  color: #1976d2;
}

/* Clickable comment count: opens the discussion drawer for the row. */
.knot-view__comment-count--btn {
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
  align-self: flex-start;
}

.knot-view__comment-count--btn:hover {
  background: #bbdefb;
  border-color: #1976d2;
}

/* Inline comment conversations (compact) */
.knot-view__comments {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.knot-view__thread {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.knot-view__comment {
  background: #f5f7f9;
  border: 1px solid #e3e8ec;
  border-radius: 6px;
  padding: 0.3rem 0.45rem;
  font-size: 0.72rem;
  line-height: 1.35;
  text-align: left;
}

.knot-view__comment--child {
  margin-left: 0.6rem;
  margin-top: 0.3rem;
  background: #fff;
}

.knot-view__comment-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.1rem;
}

.knot-view__comment-head strong {
  color: #2c3e50;
  font-weight: 600;
}

.knot-view__comment-date {
  color: #95a5a6;
  font-size: 0.65rem;
  white-space: nowrap;
}

.knot-view__comment-body {
  color: #333;
  word-break: break-word;
}

.knot-view__comment-body :deep(p) {
  margin: 0 0 0.2rem;
}

.knot-view__comment-body :deep(p:last-child) {
  margin-bottom: 0;
}

.knot-view__thread-loading {
  color: #95a5a6;
}

.knot-view__thread-more {
  align-self: flex-start;
  background: transparent;
  border: none;
  padding: 0.1rem 0.2rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #1976d2;
  cursor: pointer;
}

.knot-view__thread-more:hover {
  text-decoration: underline;
}

/* Column header toggle (e.g. show all / limited comments) */
.knot-view__col-toggle {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  padding: 0.1rem 0.25rem;
  cursor: pointer;
  border-radius: 3px;
  line-height: 1;
}

.knot-view__col-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* Empty state */
.knot-view__empty {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
}

/* Print Header - hidden on screen */
.knot-view__print-header {
  display: none;
}

/* Print Styles */
@media print {
  .knot-view {
    box-shadow: none;
    border-radius: 0;
  }

  .knot-view__header,
  .knot-view__filters,
  .knot-view__header-right {
    display: none !important;
  }

  .knot-view__print-header {
    display: block;
    padding: 1rem;
    border-bottom: 2px solid #333;
    margin-bottom: 1rem;
  }

  .knot-view__print-header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .knot-view__print-logo {
    max-height: 60px;
    max-width: 150px;
    object-fit: contain;
  }

  .knot-view__print-info {
    flex: 1;
  }

  .knot-view__print-group {
    font-size: 0.85rem;
    color: #666;
  }

  .knot-view__print-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  .knot-view__print-date {
    font-size: 0.8rem;
    color: #666;
  }

  .knot-view__print-filters {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid #ddd;
    font-size: 0.8rem;
  }

  .knot-view__print-filters span {
    margin-left: 0.5rem;
    padding: 0.1rem 0.4rem;
    background: #f0f0f0;
    border-radius: 3px;
  }

  .knot-view__summary {
    background: none;
    border: 1px solid #ddd;
    margin-bottom: 0.5rem;
  }

  .knot-view__table-wrapper {
    max-height: none;
    overflow: visible;
  }

  .knot-view__th {
    background: #333 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .knot-view__group-header {
    background: #e3f2fd !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .knot-view__group-totals {
    background: #fff3e0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .knot-view__evaluation-badge,
  .knot-view__summary-badge {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Page setup */
  @page {
    size: A4 landscape;
    margin: 1cm;
  }

  /* Avoid page breaks inside groups */
  .knot-view__group-header {
    page-break-after: avoid;
  }

  .knot-view__group-totals {
    page-break-before: avoid;
  }
}
</style>
